import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceBoard } from "@/lib/prices.functions";
import { buildSealedRows, formatMoney } from "@/lib/prices-shared";
import { CARDS_PER_PACK, HIT_SLOT_ODDS, PACKS_PER_BOX } from "@/lib/pack-engine";
import { AffiliateDisclosure, formatUtcStamp } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { ShareButton } from "@/components/ShareButton";

const URL = "https://palstars.com/pack-calculator";
const TITLE = "Palworld Pack Calculator — Pull Odds & Cost";
const DESCRIPTION =
  "Free Palworld pack calculator: enter packs or booster boxes and get expected pulls per rarity, the chance of at least one chase card, cost per hit and expected card value from live market prices.";

const STEPS = [
  { name: "Choose how much you are opening", text: "Enter a number of single packs, booster boxes, or both — one box counts as 12 packs." },
  { name: "Set your pack price", text: "The calculator seeds the live eBay asking price for a single pack and the per-pack cost of a box, and you can override either." },
  { name: "Read the odds", text: "Expected pulls per rarity come from the published hit-slot rates, and the chance of at least one hit uses the binomial complement." },
  { name: "Compare cost per hit", text: "Cost per chase card and expected card value are priced from the live Palworld TCG price chart, so you can see whether opening or buying the single is cheaper." },
];

const FAQ: FaqItem[] = [
  {
    q: "What is a Palworld pack calculator?",
    a: "It is a pull-odds and cost tool for the Palworld OFFICIAL CARD GAME. You enter how many booster packs or booster boxes you plan to open and it returns the expected number of cards at each rarity, the probability of pulling at least one of each chase rarity, the average cost per hit and the expected market value of everything you open.",
  },
  {
    q: "How many packs to pull an SSS card in Palworld TCG?",
    a: "The SSS hit rate is 0.5% of the single hit slot, so the expected wait is about 200 packs — roughly 17 booster boxes. There is a 50% chance of seeing one inside about 139 packs, which is what the chance-of-at-least-one column on this page reports for your own pack count.",
  },
  {
    q: "Is it cheaper to open packs or buy the single card?",
    a: "For chase rarities, buying the single is almost always cheaper. The calculator prices your cost per hit from the live eBay asking prices, then shows the market price of the card on the price chart so you can compare the two directly before opening anything.",
  },
  {
    q: "Where do the odds and prices come from?",
    a: "Odds are the published-style hit-slot rates used across Pal Stars: 4 Common, 2 Uncommon and 1 hit slot per 7-card pack. Card and sealed prices come from the daily validated marketplace feed.",
  },
  {
    q: "Does the pack calculator cost anything or need an account?",
    a: "No. It is free, needs no account and runs entirely in your browser. If you want to open packs for fun instead, the free Pal Stars pack opening game gives 12 virtual packs a day.",
  },
];

export const Route = createFileRoute("/pack-calculator")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld pack calculator — pull odds, cost per hit" },
      { property: "og:description", content: "Expected pulls per rarity, chance of a chase card and cost per hit from live Palworld TCG prices." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Price Chart", "/prices"], ["Pack Calculator", "/pack-calculator"]]),
      toolSchema({ name: "Palworld Pack Calculator", description: DESCRIPTION, path: "/pack-calculator" }),
      howToSchema({ name: "How to use the Palworld pack calculator", description: "Enter packs or boxes, set the pack price and read expected pulls, chase-card odds and cost per hit.", path: "/pack-calculator", steps: STEPS }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: PackCalculatorPage,
});

/** Median of a numeric list (0 when empty). */
function median(list: number[]): number {
  if (!list.length) return 0;
  const s = [...list].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function PackCalculatorPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.rows ?? [];
  const sealed = useMemo(() => buildSealedRows(data?.sealed ?? []), [data?.sealed]);

  /** Live per-pack cost: single-pack asking price, and box price / 12. */
  const livePack = sealed.find((s) => s.key === "bp01-pack");
  const liveBox = sealed.find((s) => s.key === "bp01-box");
  const defaultPack = Math.round(((livePack?.market ?? livePack?.msrp ?? 4.99)) * 100) / 100;
  const defaultBox = Math.round(((liveBox?.market ?? liveBox?.msrp ?? 59.99)) * 100) / 100;

  const [packs, setPacks] = useState(12);
  const [boxes, setBoxes] = useState(0);
  const [packPrice, setPackPrice] = useState<number | null>(null);
  const [boxPrice, setBoxPrice] = useState<number | null>(null);

  const packCost = packPrice ?? defaultPack;
  const boxCost = boxPrice ?? defaultBox;

  const totalPacks = Math.max(0, packs) + Math.max(0, boxes) * PACKS_PER_BOX;
  const totalSpend = Math.max(0, packs) * packCost + Math.max(0, boxes) * boxCost;

  /** Median live market price per printed rarity, used to value the pulls. */
  const valueByRarity = useMemo(() => {
    const buckets = new Map<string, number[]>();
    for (const r of rows) {
      const list = buckets.get(r.rarity) ?? [];
      list.push(r.market);
      buckets.set(r.rarity, list);
    }
    const out: Record<string, number> = {};
    for (const [rarity, list] of buckets) out[rarity] = median(list);
    return out;
  }, [rows]);

  const hits = useMemo(
    () =>
      HIT_SLOT_ODDS.map((o) => {
        const expected = totalPacks * o.weight;
        const atLeastOne = totalPacks > 0 ? 1 - Math.pow(1 - o.weight, totalPacks) : 0;
        const value = valueByRarity[o.rarity] ?? 0;
        const packsPerHit = 1 / o.weight;
        return {
          rarity: o.rarity,
          weight: o.weight,
          expected,
          atLeastOne,
          packsPerHit,
          costPerHit: packsPerHit * packCost,
          value,
          expectedValue: expected * value,
        };
      }),
    [totalPacks, valueByRarity, packCost],
  );

  // Commons and uncommons are guaranteed slots, so their value is deterministic.
  const commonValue = (valueByRarity["C"] ?? 0) * 4 * totalPacks;
  const uncommonValue = (valueByRarity["U"] ?? 0) * 2 * totalPacks;
  const hitValue = hits.reduce((a, h) => a + h.expectedValue, 0);
  const expectedValue = commonValue + uncommonValue + hitValue;
  const evPerPack = totalPacks ? expectedValue / totalPacks : 0;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · TCG tool</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld Pack Calculator</h1>
          <ShareButton title={"Palworld Pack Calculator — Pal Stars"} text={"Palworld booster pack odds calculator on Pal Stars."} path="/pack-calculator" />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Enter how many Palworld booster packs or booster boxes you plan to open and this calculator returns the expected
          pulls per rarity, the chance of hitting at least one card of each chase rarity, your cost per hit and the
          expected market value of everything opened. Each pack holds {CARDS_PER_PACK} cards (4 Common, 2 Uncommon, 1 hit
          slot) and a booster box holds {PACKS_PER_BOX} packs. Card and sealed values come from the live Pal Stars price
          feed — prices updated as of {formatUtcStamp(data?.updated)}.
        </p>
      </header>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">What are you opening?</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="block text-muted-foreground">Single packs</span>
            <input
              type="number"
              min={0}
              max={5000}
              value={packs}
              onChange={(e) => setPacks(Math.max(0, Math.min(5000, Number(e.target.value) || 0)))}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 tabular-nums"
            />
          </label>
          <label className="text-sm">
            <span className="block text-muted-foreground">Booster boxes ({PACKS_PER_BOX} packs)</span>
            <input
              type="number"
              min={0}
              max={500}
              value={boxes}
              onChange={(e) => setBoxes(Math.max(0, Math.min(500, Number(e.target.value) || 0)))}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 tabular-nums"
            />
          </label>
          <label className="text-sm">
            <span className="block text-muted-foreground">Price per pack (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={packCost}
              onChange={(e) => setPackPrice(Math.max(0, Number(e.target.value) || 0))}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 tabular-nums"
            />
          </label>
          <label className="text-sm">
            <span className="block text-muted-foreground">Price per box (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={boxCost}
              onChange={(e) => setBoxPrice(Math.max(0, Number(e.target.value) || 0))}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 tabular-nums"
            />
          </label>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Live defaults: {formatMoney(defaultPack, "USD")} per pack, {formatMoney(defaultBox, "USD")} per box (
          {formatMoney(defaultBox / PACKS_PER_BOX, "USD")} per pack in a box). Override either field to price your own
          local shop.
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Packs opened", value: totalPacks.toLocaleString(), sub: `${(totalPacks * CARDS_PER_PACK).toLocaleString()} cards` },
          { label: "Total spend", value: formatMoney(totalSpend, "USD"), sub: totalPacks ? `${formatMoney(totalSpend / totalPacks, "USD")} per pack` : "—" },
          {
            label: "Expected card value",
            value: formatMoney(expectedValue, "USD"),
            sub: totalPacks ? `${formatMoney(evPerPack, "USD")} per pack · ${totalSpend > 0 ? Math.round((expectedValue / totalSpend) * 100) : 0}% of spend` : "—",
          },
        ].map((k) => (
          <div key={k.label} className="glass rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{k.value}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{k.sub}</p>
          </div>
        ))}
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Expected pulls and cost per hit</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <caption className="sr-only">Expected Palworld TCG pulls per rarity for {totalPacks} packs</caption>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-2">Rarity</th>
                <th scope="col" className="py-2">Hit-slot odds</th>
                <th scope="col" className="py-2">Packs per hit</th>
                <th scope="col" className="py-2">Expected pulls</th>
                <th scope="col" className="py-2">Chance of 1+</th>
                <th scope="col" className="py-2">Cost per hit</th>
                <th scope="col" className="py-2">Market value</th>
              </tr>
            </thead>
            <tbody>
              {hits.map((h) => (
                <tr key={h.rarity} className="border-t border-border/50">
                  <th scope="row" className="py-2 text-left font-semibold">{h.rarity}</th>
                  <td className="py-2 tabular-nums">{(h.weight * 100).toFixed(1)}%</td>
                  <td className="py-2 tabular-nums">{h.packsPerHit.toFixed(h.packsPerHit >= 10 ? 0 : 1)}</td>
                  <td className="py-2 tabular-nums">{h.expected.toFixed(2)}</td>
                  <td className="py-2 tabular-nums">{(h.atLeastOne * 100).toFixed(1)}%</td>
                  <td className="py-2 tabular-nums">{formatMoney(h.costPerHit, "USD")}</td>
                  <td className="py-2 tabular-nums">{h.value ? formatMoney(h.value, "USD") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Every pack also delivers 4 Common and 2 Uncommon cards, worth about{" "}
          {formatMoney((valueByRarity["C"] ?? 0) * 4 + (valueByRarity["U"] ?? 0) * 2, "USD")} per pack at current market
          medians. Cost per hit assumes the single-pack price above; opening boxes lowers it whenever the per-pack box
          cost is cheaper. Odds describe the long run, not any individual pack.
        </p>
      </section>

      <AffiliateDisclosure updated={data?.updated} />

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">How to use it</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Next steps</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> what each single is worth.</li>
          <li><Link to="/marketplace" className="font-semibold text-primary hover:underline">Palworld TCG marketplace →</Link> pack and box prices.</li>
          <li><Link to="/pack-opening" className="font-semibold text-primary hover:underline">Free pack opening game →</Link> 12 virtual packs a day.</li>
          <li><Link to="/tcg-tools" className="font-semibold text-primary hover:underline">Palworld TCG tools →</Link> pull-rate and collection tools.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld pack calculator FAQ" />
    </div>
  );
}
